import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import { saveFile } from 'utils/saveFile';
import { UNPREVIEWABLE_FILE_TYPES } from 'static/FileType';
import './PreviewableModalBody.less';

import FileViewer from 'common/components/file-viewer';
import { useCheckDisableDownloadAsset } from 'hooks/useCheckDisableDownload';

const PreviewableModalBody = (props) => {
  const {
    link: propLink,
    fileName,
    fileType,
    isDocument,
    pdfPreview,
    mediaType,
    originLink,
    fileSize,
    currentPreview,
    previewModalType
  } = props;

  const [finalLink, setFinalLink] = useState(propLink);

  // --- PATCH START ---
  useEffect(() => {
    if (!originLink) {
      console.warn('originLink is empty. Please Reload the page...');
      // window.location.reload();
      return;
    }

    const fileType = currentPreview?.fileType || currentPreview?.fileExtension;
    if (!propLink && currentPreview?.uniqueId && fileType) {
      try {
        // Extract the base path from originLink (everything up to uniqueId)
        const match = originLink.match(
          /^(\/api\/assets\/member-certificate\/[^/]+)/
        );
        if (match && match[1]) {
          const basePath = match[1];
          const generatedLink = `${basePath}/preview.pdf/pdf/${fileType}?dl=0`;
          console.info('Generated fallback preview link:', generatedLink);
          setFinalLink(generatedLink);
        }
      } catch (err) {
        console.error('Error generating link from originLink:', err);
      }
    } else {
      setFinalLink(propLink);
    }
  }, [
    propLink,
    originLink,
    currentPreview?.fileType,
    currentPreview?.uniqueId,
  ]);

  // --- PATCH END ---

  const isEmptyFile = fileSize === 0;

  const ImgPreview = ({ link, fileName }) => (
    <div className='previewable-modal--image-wrapper'>
      <div>
        <img
          src={link}
          alt={fileName || 'image'}
          style={{ zIndex: ' 500' }}
        ></img>
      </div>
    </div>
  );

  const Iframe = ({ url }) => {
    const iframe = `<iframe style="width: ${'100vh'}; height: ${'100%'}" src="${url}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
        dangerouslySetInnerHTML={{ __html: iframe ? iframe : '' }}
      />
    );
  };

  const isRenderUnsupportedFileViewer = () => {
    return (
      UNPREVIEWABLE_FILE_TYPES.indexOf(fileType) > -1 ||
      !finalLink ||
      isEmptyFile
    );
  };

  return (
    <>
      {fileType && fileType?.toLowerCase() === 'iframe' ? (
        <Iframe url={finalLink} />
      ) : isDocument ? (
        isRenderUnsupportedFileViewer() ? (
          <UnsupportedFileViewerCustom
            currentPreview={currentPreview}
            link={finalLink}
            originLink={originLink}
            fileName={fileName}
            isEmptyFile={isEmptyFile}
            previewModalType={previewModalType}
          />
        ) : (
          <FileViewerRender
            {...props}
            link={finalLink} // pass updated link here
            currentPreview={currentPreview}
            originLink={originLink}
            fileName={fileName}
            isEmptyFile={isEmptyFile}
            previewModalType={previewModalType}
          />
        )
      ) : (
        <ImgPreview link={finalLink} fileName={fileName} />
      )}
    </>
  );
};

const FileViewerRender = (props) => {
  const {
    mediaType,
    fileType,
    pdfPreview,
    link,
    originLink,
    fileName,
    isEmptyFile,
    currentPreview,
    previewModalType
  } = props;

  const [type, setFileType] = useState('');
  const [filePath, setFilePath] = useState('');
  const [txtContent, setTxtContent] = useState('');

  const onError = (e) => {
    console.log(e, 'error in file-viewer');
  };

  const onSetFileType = () => {
    if (
      mediaType &&
      mediaType === 'Document' &&
      pdfPreview &&
      pdfPreview !== ''
    ) {
      return 'pdf';
    }
    return fileType;
  };

  const onSetFilePath = () => {
    if (
      mediaType &&
      mediaType === 'Document' &&
      pdfPreview &&
      pdfPreview !== ''
    ) {
      return fileType === 'txt' ? link : pdfPreview;
    }
    return link;
  };

  useEffect(() => {
    const type = onSetFileType();
    const path = onSetFilePath();

    if (fileType === 'pdf' && !link) {
      setFileType('unsupported');
      return;
    }

    setFileType(type);
    setFilePath(path);

    // If TXT file, fetch content instead of iframe
    if (fileType === 'txt' && path) {
      fetch(path)
        .then((res) => res.text())
        .then((text) => setTxtContent(text))
        .catch((err) => {
          console.error('Error loading TXT file:', err);
          setTxtContent('Unable to load text file.');
        });
    }
  }, [mediaType, fileType, pdfPreview, link]);

  const renderUnsupportedFile = () => (
    <UnsupportedFileViewer
      currentPreview={currentPreview}
      link={link}
      originLink={originLink}
      fileName={fileName}
      isEmptyFile={isEmptyFile}
      previewModalType={previewModalType}
    />
  );

  const renderTxtViewer = () => (
    <div className='file-viewer-txt__wrapper'>
      <div className='file-viewer-txt__content'>
        {txtContent || 'Loading text file...'}
      </div>
    </div>
  );

  return (
    <>
      {type && filePath && (
        <div className='file-viewer'>
          {fileType === 'txt' ? (
            renderTxtViewer()
          ) : (
            <FileViewer
              fileType={type}
              filePath={filePath}
              onError={onError}
              unsupportedComponent={renderUnsupportedFile}
            />
          )}
        </div>
      )}
    </>
  );
};

const UnsupportedFileViewer = ({
  link,
  originLink,
  fileName,
  isEmptyFile,
  currentPreview,
  previewModalType
}) => {
  const { checkDisableDownloadAssetDetail } = useCheckDisableDownloadAsset();

  const disableDownloadAsset = checkDisableDownloadAssetDetail(currentPreview);

  return (
    <div className='previewable-modal--unsupported-component'>
      <div className='previewable-modal--unsupported-title'>
        {isEmptyFile
          ? 'No preview available. Please download to view detail.'
          : 'The file type is not supported. You can download it right here.'}
      </div>
      <Button
        className='preview-card__modal--title-download'
        onClick={() => saveFile(originLink || link, fileName)}
        disabled={(!originLink && !link) || !(disableDownloadAsset || previewModalType === 'certificate')}
        icon={<DownloadOutlined />}
      >
        Download
      </Button>
    </div>
  );
};

const UnsupportedFileViewerCustom = ({
  currentPreview,
  link,
  originLink,
  fileName,
  isEmptyFile,
  previewModalType
}) => (
  <div className='file-viewer-unsupported__wrapper'>
    <div className='file-viewer-unsupported__content'>
      <UnsupportedFileViewer
        currentPreview={currentPreview}
        link={link}
        originLink={originLink}
        fileName={fileName}
        isEmptyFile={isEmptyFile}
        previewModalType={previewModalType}
      />
    </div>
  </div>
);

PreviewableModalBody.propTypes = {
  link: PropTypes.string,
  fileName: PropTypes.string,
  fileType: PropTypes.string,
  isDocument: PropTypes.bool,
  pdfPreview: PropTypes.string,
  mediaType: PropTypes.string,
  originLink: PropTypes.string, // origin link to preview or download
};

PreviewableModalBody.defaultProps = {
  isDocument: false,
};

export default PreviewableModalBody;